import React, { useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";

const ZODIAC = [
  "", "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

export default function CustomIntakeModal({ product, onClose }) {
  const [step, setStep] = useState(1); // 1 = intro, 2 = form
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    primary_intention: "",
    current_situation: "",
    desired_outcome: "",
    date_of_birth: "",
    others_involved: "",
    important_dates: "",
    zodiac_sign: "",
    notes: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.primary_intention || !form.current_situation || !form.desired_outcome) {
      return toast.error("Please complete the required fields marked with *");
    }
    setSubmitting(true);
    try {
      const { data: intake } = await api.post("/custom-intakes", {
        ...form,
        product_id: product.id,
      });
      const { data: checkout } = await api.post("/checkout/session", {
        item_type: "product",
        item_id: product.id,
        origin_url: window.location.origin,
        quantity: 1,
        intake_id: intake.id,
      });
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(formatError(err));
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-4 sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="custom-intake-modal"
    >
      <div className="relative w-full max-w-2xl bg-[#0A0A0A] border border-[#D4AF37]/30 my-8">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center"
          data-testid="intake-close-btn"
          aria-label="Close"
        >
          ×
        </button>

        <div className="px-6 sm:px-10 py-8 sm:py-10">
          <div className="overline text-[#D4AF37] mb-3">Custom Hand-Poured Candle</div>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">
            {step === 1 ? "Tell The Lion Chief about your candle." : "Your Intake"}
          </h2>

          {step === 1 ? (
            <div className="mt-8 space-y-6">
              <p className="text-white/70 leading-relaxed">
                Every Custom Hand-Poured Candle is individually crafted by The Lion Chief using
                carefully selected herbs, oils, prayer, and intention based on the information you
                provide. <span className="text-[#D4AF37]">No two candles are exactly alike.</span>
              </p>
              <p className="text-white/55 text-sm leading-relaxed">
                The next step is a short intake. Required fields are marked with an asterisk.
                Everything you share goes directly to The Lion Chief — and only The Lion Chief.
                Please allow 7–10 business days for preparation after payment.
              </p>
              <div className="border border-white/10 bg-black/40 px-5 py-4 text-sm text-white/70">
                <span className="font-serif text-lg text-white">${product.price.toFixed(2)}</span>
                <span className="text-white/50 ml-2">· {product.name}</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 bg-[#8B0000] hover:bg-[#A50000] text-white px-8 py-4 text-[11px] uppercase tracking-[0.2em] transition-colors"
                  data-testid="intake-continue-btn"
                >
                  Begin Intake
                </button>
                <button
                  onClick={onClose}
                  className="sm:w-auto border border-white/15 text-white/60 hover:border-white/40 hover:text-white px-8 py-4 text-[11px] uppercase tracking-[0.2em] transition-colors"
                  data-testid="intake-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-6" data-testid="intake-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
                <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} required testid="intake-full-name" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="intake-email" />
              </div>

              <Field label="Primary Intention or Goal" name="primary_intention" value={form.primary_intention} onChange={onChange} required testid="intake-intention"
                placeholder="What is this candle for?" />

              <TextArea label="Current Situation" name="current_situation" value={form.current_situation} onChange={onChange} required testid="intake-situation"
                placeholder="What's going on right now that this candle should support?" rows={3} />

              <TextArea label="Desired Outcome" name="desired_outcome" value={form.desired_outcome} onChange={onChange} required testid="intake-outcome"
                placeholder="What does the win look like for you?" rows={3} />

              <div className="border-t border-white/10 pt-6">
                <div className="overline text-[#D4AF37] mb-4">Optional — but helpful</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-6">
                  <Field label="Date of Birth" name="date_of_birth" type="date" value={form.date_of_birth} onChange={onChange} testid="intake-dob" />
                  <div>
                    <label className="overline block mb-3">Zodiac Sign</label>
                    <select
                      name="zodiac_sign"
                      value={form.zodiac_sign}
                      onChange={onChange}
                      className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#D4AF37] outline-none"
                      data-testid="intake-zodiac"
                    >
                      {ZODIAC.map((z) => (
                        <option key={z} value={z} className="bg-black">{z || "Select…"}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <Field label="Names of Other People Involved" name="others_involved" value={form.others_involved} onChange={onChange} testid="intake-others"
                    placeholder="First names only is fine" />
                </div>
                <div className="mt-6">
                  <Field label="Important Dates" name="important_dates" value={form.important_dates} onChange={onChange} testid="intake-dates"
                    placeholder="Anniversaries, deadlines, court dates, etc." />
                </div>
                <div className="mt-6">
                  <TextArea label="Additional Notes for The Lion Chief" name="notes" value={form.notes} onChange={onChange} testid="intake-notes"
                    placeholder="Anything else you'd like Him to know" rows={3} />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-6 border-t border-white/10">
                <div>
                  <div className="overline text-[#D4AF37]">Due Now</div>
                  <div className="font-serif text-3xl">${product.price.toFixed(2)}</div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#8B0000] hover:bg-[#A50000] disabled:opacity-50 text-white px-10 py-4 text-[11px] uppercase tracking-[0.2em]"
                  data-testid="intake-submit-btn"
                >
                  {submitting ? "Processing…" : "Continue to Payment"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="overline block mb-3">{label}{required && " *"}</label>
      <input
        type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder}
        className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none"
        data-testid={testid}
      />
    </div>
  );
}

function TextArea({ label, name, value, onChange, required, placeholder, rows = 3, testid }) {
  return (
    <div>
      <label className="overline block mb-3">{label}{required && " *"}</label>
      <textarea
        name={name} value={value} onChange={onChange} required={required} placeholder={placeholder} rows={rows}
        className="w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none resize-none"
        data-testid={testid}
      />
    </div>
  );
}
